import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  ApiError,
  answerGate,
  sendSignInCode,
  getSession,
  openLink,
  sessionCode,
  replaceDocument,
  resumeRequirement,
  skipRequirement,
  sendMessage,
  startRequirement,
  uploadDocument,
  type Challenge,
  type Session,
} from "@sdcp/api-client";

import { Composer } from "./components/Composer";
import { Conversation } from "./components/Conversation";
import { Gate } from "./components/Gate";
import { Header } from "./components/Header";
import { isInFlight, TaskStrip } from "./components/TaskStrip";

export default function App() {
  return (
    <Routes>
      <Route path="/s/:code" element={<Redeem />} />
      <Route path="/" element={<CollectionSession />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Redeem a magic link, then leave the URL behind.
 *
 * The code is replaced in history rather than pushed, so the back button cannot
 * return to a URL containing it, and a shared screenshot of the address bar
 * shows nothing useful (ADR-0001).
 */
/**
 * Opening a magic link.
 *
 * The server answers with either the session or the factor guarding it, never
 * both — so this either lands the borrower in their collection or hands them a
 * challenge. The challenge screen is rendered here rather than routed to,
 * because the code lives in this component's URL and nothing else needs it.
 */
function Redeem() {
  const { code } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [gateError, setGateError] = useState<string | null>(null);

  const land = (session: Session) => {
    queryClient.setQueryData(["session"], session);
    // The code leaves the address bar either way. It travels as a header
    // afterwards, so it stays out of Referer headers and history (ADR-0001).
    navigate("/", { replace: true });
  };

  const { isError, error } = useQuery({
    queryKey: ["redeem", code],
    queryFn: async () => {
      const entry = await openLink(code!);
      if (entry.session) land(entry.session);
      else if (entry.challenge) setChallenge(entry.challenge);
      return entry;
    },
    retry: false,
    enabled: Boolean(code),
  });

  const answer = useMutation({
    mutationFn: answerGate,
    onSuccess: (entry) => {
      setGateError(null);
      if (entry.session) land(entry.session);
    },
    onError: (e: unknown) => {
      // The server's text names the tries left and is written for the borrower,
      // so it is shown as-is. The count comes down from the same source rather
      // than being decremented locally, which would drift on a failed request.
      setGateError(e instanceof ApiError ? e.message : "That did not work.");
      setChallenge((c) =>
        c
          ? { ...c, attempts_remaining: Math.max(0, c.attempts_remaining - 1) }
          : c,
      );
    },
  });

  const send = useMutation({
    mutationFn: sendSignInCode,
    onSuccess: (entry) => {
      setGateError(null);
      if (entry.challenge) setChallenge(entry.challenge);
    },
    onError: (e: unknown) => {
      setGateError(
        e instanceof ApiError ? e.message : "We couldn't send the code.",
      );
    },
  });

  if (isError) return <Problem error={error} />;
  if (challenge)
    return (
      <Gate
        challenge={challenge}
        onSubmit={(value) => answer.mutate(value)}
        onSendCode={() => send.mutate()}
        busy={answer.isPending}
        sending={send.isPending}
        error={gateError}
      />
    );
  return <Splash>Opening your document request…</Splash>;
}

function CollectionSession() {
  const queryClient = useQueryClient();
  const hasCode = Boolean(sessionCode.get());

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    enabled: hasCode,
    retry: false,
  });

  const start = useMutation({
    mutationFn: startRequirement,
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  const [connectError, setConnectError] = useState<string | null>(null);

  // The borrower's own words. Verity previously only ever heard a sentence we
  // wrote for it, which left most of its tools unreachable from the interface.
  const say = useMutation({
    mutationFn: sendMessage,
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  const skip = useMutation({
    mutationFn: skipRequirement,
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  const replaceDoc = useMutation({
    mutationFn: replaceDocument,
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  const resume = useMutation({
    mutationFn: resumeRequirement,
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  const upload = useMutation({
    mutationFn: ({
      requirementId,
      file,
    }: {
      requirementId: string;
      file: File;
    }) => uploadDocument(requirementId, file),
    onSuccess: (session: Session) =>
      queryClient.setQueryData(["session"], session),
  });

  // Retrieval continues server-side after the request returns, so the view is
  // refreshed while anything is in flight. Replaced by SSE next.
  const inFlight = (data?.requirements ?? []).filter(isInFlight);
  useEffect(() => {
    if (inFlight.length === 0) return;
    const id = window.setInterval(
      () => queryClient.invalidateQueries({ queryKey: ["session"] }),
      3000,
    );
    return () => window.clearInterval(id);
  }, [inFlight.length, queryClient]);

  if (!hasCode) {
    return (
      <Problem
        error={
          new ApiError(
            401,
            null,
            "Open the link your loan officer sent you to continue.",
          )
        }
      />
    );
  }
  if (isPending) return <Splash>Loading…</Splash>;
  if (isError || !data) return <Problem error={error} />;

  const choice = data.choice ?? null;
  const activeRequirement = data.requirements.find(
    (r) => r.id === choice?.requirement_id,
  );

  return (
    <div className="mx-auto flex h-dvh max-w-[430px] flex-col bg-bg-app shadow-[var(--e-2)]">
      <Header officerName={data.officer_name} />
      <TaskStrip requirements={data.requirements} />
      <Conversation
        turns={data.turns}
        verified={data.verified_count}
        total={data.total_count}
        loanReference={data.loan_reference}
        requirements={data.requirements}
        onReplace={(id) => replaceDoc.mutate(id)}
        onResume={(id: string) => resume.mutate(id)}
        busy={replaceDoc.isPending}
        replying={
          start.isPending || skip.isPending || resume.isPending || say.isPending
        }
        pendingText={say.isPending ? (say.variables ?? null) : null}
      />
      {choice && (
        <Composer
          choice={choice}
          requirement={activeRequirement}
          busy={
            start.isPending ||
            upload.isPending ||
            skip.isPending ||
            resume.isPending ||
            say.isPending
          }
          error={
            connectError ??
            (upload.error instanceof ApiError ? upload.error.message : null)
          }
          onPrimary={() =>
            choice.requirement_id && start.mutate(choice.requirement_id)
          }
          onSkip={() =>
            choice.requirement_id && skip.mutate(choice.requirement_id)
          }
          onUpload={(file) =>
            choice.requirement_id &&
            upload.mutate({ requirementId: choice.requirement_id, file })
          }
          onConnected={(session) => {
            setConnectError(null);
            queryClient.setQueryData(["session"], session);
          }}
          onError={setConnectError}
          onSay={(text) => say.mutate(text)}
        />
      )}
    </div>
  );
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex h-dvh max-w-[430px] items-center justify-center bg-bg-app">
      <p className="text-sm text-ink-500">{children}</p>
    </div>
  );
}

function Problem({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? error.message
      : "Something went wrong. Please try again.";
  const requestId = error instanceof ApiError ? error.requestId : null;

  return (
    <div className="mx-auto flex h-dvh max-w-[430px] flex-col items-center justify-center gap-3 bg-bg-app px-8 text-center">
      <p className="text-lg font-bold">{message}</p>
      <p className="text-sm text-ink-500">
        Your loan officer can send a new link if you need one.
      </p>
      {/* The reference, not the error detail — this is what support can trace. */}
      {requestId && (
        <p className="font-mono text-[11px] text-ink-350">{requestId}</p>
      )}
    </div>
  );
}
